// src/v2/playbooks/PlaybookLaunchModal.jsx
//
// G-34-S2: Playbook launch modal.
//
// 3-step flow: template select → context → review & launch.
// Pre-selects a template when launched from a tile's "Start →" button.
// On success, calls onLaunched(runId) so the caller can navigate to the run.

import React, { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { T } from "../../app/tokens.js";
import { APP_NAME } from "../../constants/branding.js";
import { DrawerCloseButton } from "../components/DrawerCloseButton.jsx";
import { getPlaybookTemplates, launchPlaybookRun } from "../../data/playbooksClient.js";
import { showToast } from "../../components/toastService.jsx";
import { log } from "../../utils/logger.js";

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = [
  { key: "template", label: "Choose playbook"  },
  { key: "context",  label: "Add context"      },
  { key: "review",   label: "Review & launch"  },
];

// ─── Target type options ──────────────────────────────────────────────────────

const TARGET_TYPES = [
  { value: "office", label: "Office" },
  { value: "lease",  label: "Lease"  },
  { value: "market", label: "Market" },
];

// ─── StepBar ──────────────────────────────────────────────────────────────────

function StepBar({ stepIndex }) {
  return (
    <div
      data-testid="launch-modal-step-bar"
      style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 22 }}
    >
      {STEPS.map((s, i) => {
        const done   = i < stepIndex;
        const active = i === stepIndex;
        return (
          <React.Fragment key={s.key}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div
                style={{
                  width: 22, height: 22, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: T.fsXxs, fontWeight: T.fwBold,
                  background: done ? T.green : active ? T.blue : T.bg,
                  color: done || active ? T.white : T.mid,
                  border: active ? `2px solid ${T.blue}` : `1px solid ${T.border}`,
                  flexShrink: 0,
                }}
              >
                {done ? <Check size={11} /> : i + 1}
              </div>
              <span style={{
                fontSize: T.fsXs,
                fontWeight: active ? T.fwBold : T.fwRegular,
                color: active ? T.dark : T.mid,
                whiteSpace: "nowrap",
              }}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 1, background: T.border, minWidth: 12 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── fieldStyle ───────────────────────────────────────────────────────────────

function fieldStyle() {
  return {
    width: "100%", boxSizing: "border-box",
    padding: "8px 10px",
    fontSize: T.fsSm5,
    border: `1px solid ${T.border}`,
    borderRadius: 7,
    color: T.dark,
    background: T.white,
  };
}

// ─── Step 1: Template selector ────────────────────────────────────────────────

function StepTemplate({ templates, selectedId, onSelect }) {
  return (
    <div data-testid="launch-step-template">
      <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>
        Choose a playbook
      </div>
      <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 16 }}>
        Select the action type to launch.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {templates.map((t) => {
          const selected = t.id === selectedId;
          return (
            <div
              key={t.id}
              data-testid={`launch-template-card-${t.id}`}
              onClick={() => onSelect(t.id)}
              style={{
                border: `2px solid ${selected ? T.blue : T.border}`,
                background: selected ? T.blueSoft : T.white,
                borderRadius: 10, padding: "12px 14px",
                cursor: "pointer",
                display: "flex", alignItems: "flex-start", gap: 11,
                transition: "border-color .15s",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.dark, marginBottom: 3 }}>
                  {t.name}
                  {t.status === "draft" && (
                    <span style={{
                      marginLeft: 8,
                      fontSize: T.fsXxs, fontWeight: T.fwBold,
                      background: T.ragAmberBg, color: T.ragAmberText,
                      border: `1px solid ${T.ragAmberBorder}`,
                      borderRadius: 5, padding: "1px 6px",
                    }}>
                      Draft
                    </span>
                  )}
                </div>
                <div style={{ fontSize: T.fsXs, color: T.mid }}>{t.description}</div>
                <div style={{ fontSize: T.fsXxs, color: T.light, marginTop: 5, fontWeight: T.fwSemibold }}>
                  {t.checkCount} checks · {t.phaseCount} phases
                  {t.appliesto && ` · ${t.appliesto}`}
                </div>
              </div>
              {selected && <Check size={16} color={T.blue} style={{ flexShrink: 0, marginTop: 2 }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 2: Context form ─────────────────────────────────────────────────────

function StepContext({ form, onChange, users }) {
  return (
    <div data-testid="launch-step-context">
      <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>
        Add context
      </div>
      <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 16 }}>
        Tell {APP_NAME} what this run covers.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
        {/* Target type */}
        <label style={{ fontSize: T.fsXs, color: T.mid, display: "block" }}>
          Target type
          <select
            data-testid="launch-input-target-type"
            style={{ ...fieldStyle(), marginTop: 4 }}
            value={form.sourceType}
            onChange={(e) => onChange("sourceType", e.target.value)}
          >
            <option value="">Select type…</option>
            {TARGET_TYPES.map((tt) => (
              <option key={tt.value} value={tt.value}>{tt.label}</option>
            ))}
          </select>
        </label>

        {/* Target label */}
        <label style={{ fontSize: T.fsXs, color: T.mid, display: "block" }}>
          Target (office, lease, or market name)
          <input
            type="text"
            data-testid="launch-input-target-label"
            style={{ ...fieldStyle(), marginTop: 4 }}
            value={form.sourceLabel}
            onChange={(e) => onChange("sourceLabel", e.target.value)}
            placeholder="e.g. LON-02 Hans Crescent…"
          />
        </label>

        {/* Owner */}
        <label style={{ fontSize: T.fsXs, color: T.mid, display: "block" }}>
          Owner
          <select
            data-testid="launch-input-owner"
            style={{ ...fieldStyle(), marginTop: 4 }}
            value={form.ownerName}
            onChange={(e) => onChange("ownerName", e.target.value)}
          >
            <option value="">Unassigned</option>
            {users.map((u) => (
              <option key={u.id} value={u.displayName}>{u.displayName}</option>
            ))}
          </select>
        </label>
      </div>

      <div style={{
        marginTop: 16,
        background: T.blueSoft,
        border: `1px solid ${T.blue}`,
        borderRadius: 9, padding: "10px 13px",
        fontSize: T.fsXs, color: T.blue, fontWeight: T.fwSemibold,
      }}>
        {APP_NAME} assembles the run from live records so all fact checks
        reflect real state from the moment you launch.
      </div>
    </div>
  );
}

// ─── Step 3: Review ───────────────────────────────────────────────────────────

function StepReview({ form, templates }) {
  const tmpl = templates.find((t) => t.id === form.templateId);
  const rows = [
    ["Playbook",  tmpl?.name ?? "—"],
    ["Target",    form.sourceLabel || "—"],
    ["Type",      form.sourceType  || "—"],
    ["Owner",     form.ownerName   || "Unassigned"],
  ];

  return (
    <div data-testid="launch-step-review">
      <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>
        Review &amp; launch
      </div>
      <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 16 }}>
        Confirm the details below before launching the run.
      </div>

      <div style={{
        border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden",
        marginBottom: 14,
      }}>
        {rows.map(([label, value], i) => (
          <div
            key={label}
            style={{
              display: "flex", justifyContent: "space-between",
              padding: "10px 14px",
              borderBottom: i < rows.length - 1 ? `1px solid ${T.border}` : "none",
            }}
          >
            <span style={{ fontSize: T.fsXs, color: T.mid }}>{label}</span>
            <span style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: T.dark }}>{value}</span>
          </div>
        ))}
      </div>

      <div style={{
        background: T.greenSoft,
        border: `1px solid ${T.green}`,
        borderRadius: 9, padding: "10px 13px",
        fontSize: T.fsXs, color: T.dark, fontWeight: T.fwSemibold,
      }}>
        <span style={{ color: T.green }}>✓</span>{" "}
        Once launched, this run appears in <strong>Running now</strong> on the
        Playbooks home and tracks its roadmap here.
      </div>
    </div>
  );
}

// ─── PlaybookLaunchModal ──────────────────────────────────────────────────────

export function PlaybookLaunchModal({ initialTemplateId = null, onClose, onLaunched }) {
  const [stepIndex,   setStepIndex]   = useState(0);
  const [templates,   setTemplates]   = useState([]);
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState(null);

  const [form, setForm] = useState({
    templateId:  initialTemplateId,
    sourceType:  "",
    sourceLabel: "",
    ownerName:   "",
  });

  const onChange = useCallback((field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  }, []);

  // Load templates + users on mount
  useEffect(() => {
    let cancelled = false;

    Promise.all([
      getPlaybookTemplates(),
      fetch("/api/plan/users").then((r) => r.ok ? r.json() : []).catch(() => []),
    ]).then(([tmpls, usrs]) => {
      if (cancelled) return;
      setTemplates(Array.isArray(tmpls) ? tmpls : []);
      setUsers(Array.isArray(usrs) ? usrs : []);
      // Auto-select first template if none pre-selected
      if (!initialTemplateId && tmpls.length > 0) {
        setForm((f) => ({ ...f, templateId: tmpls[0].id }));
      }
      setLoading(false);
    }).catch((err) => {
      if (!cancelled) {
        log.error("PlaybookLaunchModal: failed to load data", err);
        setLoading(false);
        setError("Could not load playbooks. Please try again.");
      }
    });

    return () => { cancelled = true; };
  }, [initialTemplateId]);

  const goNext = useCallback(() => setStepIndex((i) => Math.min(i + 1, STEPS.length - 1)), []);
  const goBack = useCallback(() => { setError(null); setStepIndex((i) => Math.max(i - 1, 0)); }, []);

  async function handleLaunch() {
    if (!form.templateId) return;
    setSubmitting(true);
    setError(null);
    try {
      const tmpl = templates.find((t) => t.id === form.templateId);
      // API requires sourceId when sourceType is set. Use sourceLabel as the
      // freeform ID so the validation gate passes for manually-entered targets.
      const hasSource = !!(form.sourceType && form.sourceLabel);
      const run = await launchPlaybookRun({
        templateId:  form.templateId,
        name:        tmpl ? `${tmpl.name}${form.sourceLabel ? ` — ${form.sourceLabel}` : ""}` : "Playbook run",
        ownerName:   form.ownerName  || null,
        sourceType:  hasSource ? form.sourceType  : null,
        sourceId:    hasSource ? form.sourceLabel : null,
        sourceLabel: hasSource ? form.sourceLabel : null,
      });
      showToast("Playbook launched.", { type: "success" });
      onLaunched(run.id);
    } catch (err) {
      log.error("PlaybookLaunchModal: failed to launch", err);
      setError("Launch failed. Please try again.");
      setSubmitting(false);
    }
  }

  const currentStep = STEPS[stepIndex].key;
  const canProceed  = stepIndex === 0 ? !!form.templateId : true;

  return (
    /* Overlay backdrop */
    <div
      data-testid="launch-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0,
        background: T.modalOverlay,
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1200,
      }}
    >
      <div
        data-testid="launch-modal"
        style={{
          position: "relative",
          width: 520, maxHeight: "90vh",
          background: T.white,
          borderRadius: 16,
          boxShadow: T.shadowModal,
          display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "18px 22px 14px",
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
        }}>
          <div style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.dark, marginBottom: 12 }}>
            New playbook run
          </div>
          <StepBar stepIndex={stepIndex} />
        </div>
        <DrawerCloseButton onClick={onClose} testId="launch-modal-close" />

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", padding: "18px 22px" }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 120 }}>
              <Loader2 size={20} style={{ color: T.mid, animation: "spin 1s linear infinite" }} />
            </div>
          ) : error && stepIndex === 0 ? (
            <div style={{ fontSize: T.fsSm5, color: T.red, padding: "12px 0" }}>{error}</div>
          ) : currentStep === "template" ? (
            <StepTemplate templates={templates} selectedId={form.templateId} onSelect={(id) => onChange("templateId", id)} />
          ) : currentStep === "context" ? (
            <StepContext form={form} onChange={onChange} users={users} />
          ) : (
            <StepReview form={form} templates={templates} />
          )}
          {error && stepIndex > 0 && (
            <div style={{ marginTop: 12, fontSize: T.fsSm5, color: T.red }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 22px",
          borderTop: `1px solid ${T.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexShrink: 0,
        }}>
          {stepIndex > 0 ? (
            <button
              type="button"
              data-testid="launch-modal-back"
              onClick={goBack}
              style={{
                background: "transparent", border: `1px solid ${T.border}`,
                borderRadius: 8, padding: "8px 16px",
                fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.mid,
                cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              }}
            >
              <ArrowLeft size={14} /> Back
            </button>
          ) : (
            <div />
          )}

          {stepIndex < STEPS.length - 1 ? (
            <button
              type="button"
              data-testid="launch-modal-next"
              onClick={goNext}
              disabled={!canProceed}
              style={{
                background: canProceed ? T.blue : T.border,
                border: "none", borderRadius: 8, padding: "8px 18px",
                fontSize: T.fsSm5, fontWeight: T.fwBold,
                color: canProceed ? T.white : T.light,
                cursor: canProceed ? "pointer" : "not-allowed",
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              Continue <ArrowRight size={14} />
            </button>
          ) : (
            <button
              type="button"
              data-testid="launch-modal-launch"
              onClick={handleLaunch}
              disabled={submitting}
              style={{
                background: submitting ? T.border : T.blue,
                border: "none", borderRadius: 8, padding: "8px 18px",
                fontSize: T.fsSm5, fontWeight: T.fwBold,
                color: T.white, cursor: submitting ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              {submitting ? <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} /> : null}
              Launch run →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
